/**
 * Mock implementation of Brand Health Service V2
 * Returns mock data instead of making API calls
 */

import {
  mockBrands,
  mockLatestEquity,
  mockEquityTrends,
  mockAwarenessFunnel,
  mockChannels,
  mockPositioning,
  mockDigitalMetrics,
  mockSentiment,
  mockGoogleTrends,
  mockLoyalty,
  mockFilterOptions,
  mockAwarenessQuestions,
  mockPositioningQuestions,
  mockChannelList,
  mockSocialChannels
} from './mockData';

import type {
  Brand,
  EquityData,
  FunnelData,
  ChannelData,
  PositioningData,
  DigitalMetrics,
  SentimentData,
  LoyaltyData,
  FilterOptions
} from './brandHealthServiceV2';

// Helper to simulate async API call
async function mockApiCall<T>(data: T, delay: number = 300): Promise<T> {
  return new Promise(resolve => {
    setTimeout(() => resolve(data), delay);
  });
}

/**
 * Get all brands for the organization
 */
export async function getBrands(): Promise<Brand[]> {
  return mockApiCall(mockBrands);
}

/**
 * Get a single brand's details
 */
export async function getBrand(brandId: string): Promise<Brand> {
  const brand = mockBrands.find(b => b.brand_id === brandId);
  if (!brand) {
    throw new Error(`Brand ${brandId} not found`);
  }
  return mockApiCall(brand);
}

/**
 * Get latest equity index for all brands
 */
export async function getLatestEquity(): Promise<EquityData[]> {
  return mockApiCall(mockLatestEquity);
}

/**
 * Get equity trends over time
 */
export async function getEquityTrends(
  months: number = 6
): Promise<{ [brand_id: string]: EquityData[] }> {
  return mockApiCall(mockEquityTrends);
}

/**
 * Get awareness funnel data with optional filters
 */
export async function getAwarenessFunnel(filters?: any): Promise<FunnelData> {
  // In mock mode, we ignore filters and return full data
  return mockApiCall(mockAwarenessFunnel);
}

/**
 * Compare brand awareness for a specific funnel stage
 */
export async function compareAwareness(
  question: string = 'Aided awareness'
): Promise<{
  question: string;
  total_respondents: number;
  comparison: { brand_id: string; count: number; percentage: number }[];
}> {
  const comparison = Object.entries(mockAwarenessFunnel.funnel).map(([brand_id, funnel]) => {
    const stage = funnel[question] || { count: 0, percentage: 0 };
    return {
      brand_id,
      count: stage.count,
      percentage: stage.percentage
    };
  });

  return mockApiCall({
    question,
    total_respondents: mockAwarenessFunnel.total_respondents,
    comparison
  });
}

/**
 * Get channel distribution
 */
export async function getChannels(brand?: string): Promise<ChannelData> {
  if (brand) {
    const brandChannels = mockChannels[brand];
    return mockApiCall(brandChannels ? { [brand]: brandChannels } : {});
  }
  return mockApiCall(mockChannels);
}

/**
 * Get positioning data
 */
export async function getPositioning(
  brand?: string,
  category?: string
): Promise<PositioningData> {
  if (brand) {
    const brandPositioning = mockPositioning[brand];
    return mockApiCall(brandPositioning ? { [brand]: brandPositioning } : {});
  }
  return mockApiCall(mockPositioning);
}

/**
 * Compare positioning for a specific statement
 */
export async function comparePositioning(
  question: string
): Promise<{
  question: string;
  total: number;
  comparison: { brand_id: string; count: number; percentage: number }[];
}> {
  const comparison: { brand_id: string; count: number; percentage: number }[] = [];
  let total = 0;

  Object.entries(mockPositioning).forEach(([brand_id, statements]) => {
    const statement = statements.find(s => s.statement === question);
    if (statement) {
      comparison.push({
        brand_id,
        count: statement.count,
        percentage: (statement.count / 1250) * 100 // Mock total respondents
      });
      total += statement.count;
    }
  });

  return mockApiCall({
    question,
    total,
    comparison
  });
}

/**
 * Get demographic breakdown
 */
export async function getDemographicsBreakdown(
  dimension: 'gender' | 'age' | 'city' | 'nationality' | 'sec' = 'gender',
  brand?: string
): Promise<{
  dimension: string;
  breakdown: {
    [brand_id: string]: {
      [key: string]: string | number;
      avg_equity: number;
      count: number;
    }[];
  };
}> {
  // Generate mock demographic breakdown
  const breakdown: any = {};

  const dimensionValues = dimension === 'gender' ? ['Male', 'Female'] :
                         dimension === 'age' ? ['18-24', '25-34', '35-44', '45-54', '55+'] :
                         dimension === 'city' ? ['Cairo', 'Dubai', 'Riyadh'] :
                         ['UAE', 'Saudi Arabia', 'Egypt'];

  mockBrands.forEach(brandObj => {
    if (brand && brandObj.brand_id !== brand) return;

    const brandEquity = mockLatestEquity.find(e => e.brand_id === brandObj.brand_id)!;
    breakdown[brandObj.brand_id] = dimensionValues.map(value => ({
      [dimension]: value,
      avg_equity: brandEquity.total_equity + (Math.random() - 0.5) * 10,
      count: Math.floor(Math.random() * 200) + 50
    }));
  });

  return mockApiCall({
    dimension,
    breakdown
  });
}

/**
 * Get digital engagement metrics
 */
export async function getDigitalMetrics(
  brand?: string,
  channel?: string,
  start_date?: string,
  end_date?: string
): Promise<DigitalMetrics[]> {
  let filtered = mockDigitalMetrics;

  if (brand) {
    filtered = filtered.filter(m => m.brand_id === brand);
  }

  if (channel) {
    filtered = filtered.filter(m => m.channel === channel);
  }

  return mockApiCall(filtered);
}

/**
 * Get sentiment analysis
 */
export async function getSentiment(
  brand?: string,
  channel?: string,
  start_date?: string,
  end_date?: string
): Promise<SentimentData> {
  if (brand) {
    const brandSentiment = mockSentiment[brand];
    return mockApiCall(brandSentiment ? { [brand]: brandSentiment } : {});
  }
  return mockApiCall(mockSentiment);
}

/**
 * Get Google Trends data
 */
export async function getGoogleTrends(
  brand?: string,
  start_date?: string,
  end_date?: string
): Promise<{ [brand: string]: { date: string; score: number }[] }> {
  if (brand) {
    const brandTrends = mockGoogleTrends[brand];
    return mockApiCall(brandTrends ? { [brand]: brandTrends } : {});
  }
  return mockApiCall(mockGoogleTrends);
}

/**
 * Get loyalty and NPS metrics
 */
export async function getLoyalty(brand?: string): Promise<LoyaltyData[]> {
  if (brand) {
    const brandLoyalty = mockLoyalty.filter(l => l.brand_id === brand);
    return mockApiCall(brandLoyalty);
  }
  return mockApiCall(mockLoyalty);
}

/**
 * Get all filter options
 */
export async function getFilterOptions(): Promise<FilterOptions> {
  return mockApiCall(mockFilterOptions);
}

/**
 * Get awareness funnel questions
 */
export async function getAwarenessQuestions(): Promise<string[]> {
  return mockApiCall(mockAwarenessQuestions);
}

/**
 * Get positioning questions
 */
export async function getPositioningQuestions(): Promise<string[]> {
  return mockApiCall(mockPositioningQuestions);
}

/**
 * Get channel list
 */
export async function getChannelList(): Promise<string[]> {
  return mockApiCall(mockChannelList);
}

/**
 * Get social media channels
 */
export async function getSocialChannels(): Promise<string[]> {
  return mockApiCall(mockSocialChannels);
}

// Default export
const brandHealthServiceV2Mock = {
  getBrands,
  getBrand,
  getLatestEquity,
  getEquityTrends,
  getAwarenessFunnel,
  compareAwareness,
  getChannels,
  getPositioning,
  comparePositioning,
  getDemographicsBreakdown,
  getDigitalMetrics,
  getSentiment,
  getGoogleTrends,
  getLoyalty,
  getFilterOptions,
  getAwarenessQuestions,
  getPositioningQuestions,
  getChannelList,
  getSocialChannels,
};

export default brandHealthServiceV2Mock;
